import { ErrorHandlerService } from '@loavies/utils'
import { MarketplaceTransactionQueryModel } from '@loavies/c2c-models'

import { mapRelations } from '../response-mappers/relations-mapper'
import { mapTransaction, mapTransactions } from '../response-mappers/transaction-mappers'
import { MarketplaceUserService } from './marketplace-user-service'
import { MarketplaceTransitionService } from './marketplace-transition-service'
import { PAGINATION } from './constants/pagination'

export class MarketplaceTransactionsService {
  constructor(marketplaceService) {
    this.marketplaceService = marketplaceService
    this.marketplace = marketplaceService.instance
    this.errorHandlerService = new ErrorHandlerService()
    this.queryIncludes = this.marketplaceService.getTransactionQueryIncludes()
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#show-transaction
   * @param {string} transactionId
   * @param {string} [userId]
   * @return {Promise<loavies.models.transaction.TransactionModel>}
   */
  getTransactionById(transactionId, userId) {
    return this.marketplace.transactions
      .show({
        id: transactionId,
        include: this.queryIncludes,
        ...this.marketplaceService.getImageVariants(),
      })
      .then(response => mapRelations(response.data))
      .then(async response => {
        const currentUserId = userId ?? await this.getUserId()

        return mapTransaction(response.data, currentUserId)
      })
      .then(transaction => this.getTransactionEnrichedWithNextTransitions(transaction))
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }

  /**
   * @param {string} userId
   * @return {Promise<loavies.models.transaction.TransactionModel[]>}
   */
  getAllTransactionsByUserId(userId) {
    return this.queryAll({ userId })
      .then(transactions => this.getTransactionsEnrichedWithNextTransitions(transactions))
  }

  /**
   * @param {string} userId
   * @return {Promise<loavies.models.transaction.TransactionModel[]>}
   */
  getMyOrderTransactions(userId) {
    return this.queryAll({
      query: new MarketplaceTransactionQueryModel({
        only: 'order',
        lastTransitions: this.marketplaceService.getEligibleLastTransitionNamesForCustomer(),
      }),
      userId,
    })
  }

  /**
   * @param {string} userId
   * @return {Promise<loavies.models.transaction.TransactionModel[]>}
   */
  getMySaleTransactions(userId) {
    return this.queryAll({
      query: new MarketplaceTransactionQueryModel({
        only: 'sale',
        lastTransitions: this.marketplaceService.getEligibleLastTransitionNamesForProvider(),
      }),
      userId,
    })
  }

  /**
   * Returns immutable transactions enriched with last transitions
   * @param {loavies.models.transaction.TransactionModel[]} transactions
   * @returns {Promise<loavies.models.transaction.TransactionModel[]>}
   */
  async getTransactionsEnrichedWithNextTransitions(transactions) {
    return Promise.all(transactions.map(transaction => this.getTransactionEnrichedWithNextTransitions(transaction)))
  }

  /**
   * Returns immutable transaction enriched with last transitions
   * @param {loavies.models.transaction.TransactionModel} transaction
   * @returns {Promise<loavies.models.transaction.TransactionModel>}
   */
  async getTransactionEnrichedWithNextTransitions(transaction) {
    const marketplaceTransitionService = new MarketplaceTransitionService(this.marketplaceService)
    const transactionCopy = transaction.deepCopy()

    transactionCopy.nextTransitions = await marketplaceTransitionService.getNextTransitions(transaction.id)

    return transactionCopy
  }

  /**
   * @return {Promise<string>}
   */
  getUserId() {
    return new MarketplaceUserService(this.marketplaceService)
      .getCurrentUser()
      .then(user => user.id)
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#query-transactions
   * @param {string} [transition]
   * @param {string[]} [transitions]
   * @param {string} only - One of sale or order. sale means that the authenticated user is the provider in the transaction
   * and order means that the user is customer in the transaction.
   * @param {string} [userId]
   */
  getTransactionsByLastTransitions({ transition, transitions, only = 'sale', userId }) {
    if (!transition && !transitions) {
      return Promise.reject(Error('Missing transition'))
    }

    const lastTransitions = transition
      ? [transition]
      : transitions

    return this.queryAll({
      query: new MarketplaceTransactionQueryModel({
        only,
        lastTransitions,
      }),
      userId,
    })
  }

  /**
   * Returns all transactions without pagination
   * https://www.sharetribe.com/api-reference/marketplace.html#query-transactions
   * @param {loavies.models.transaction.MarketplaceTransactionQueryModel} [query]
   * @param {string} [userId]
   * @return {Promise<loavies.models.transaction.TransactionModel[]>}
   */
  async queryAll({ query = new MarketplaceTransactionQueryModel({}), userId }) {
    try {
      const localeUserId = userId ? userId : await this.getUserId()
      let totalPages
      let transactions

      await this.query({
          query,
          perPage: PAGINATION.MAXIMUM_PAGE_SIZE,
        })
        .then(response => {
          totalPages = response.meta.totalPages
          transactions = mapTransactions(response.data, localeUserId)
        })

      if (totalPages === 1) {
        return transactions
      }

      const promises = []

      // Starting at page 2
      for (let i = 2; i <= totalPages; i++) {
        promises.push(
          this.query({
              query,
              page: i,
              perPage: PAGINATION.MAXIMUM_PAGE_SIZE,
            })
        )
      }

      await Promise.all(promises).then(response => {
        const pages = response.map(page => mapTransactions(page.data, localeUserId)).flat()
        transactions = [...transactions, ...pages]
      })

      return transactions
    } catch (error) {
      return Promise.reject(error)
    }
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#query-transactions
   * @param {loavies.models.transaction.MarketplaceTransactionQueryModel} [query]
   * @param {number} [page]
   * @param {number} [perPage]
   * @return {Promise<{
   *  data: Array,
   *  meta: Object,
   * }>}
   */
  async query({
    query = new MarketplaceTransactionQueryModel({}),
    page = PAGINATION.DEFAULT_PAGE,
    perPage = PAGINATION.DEFAULT_ITEMS_PER_PAGE,
  } = {}) {
    return this.marketplace.transactions
      .query({
        ...query,
        include: this.queryIncludes,
        ...this.marketplaceService.getImageVariants(),
        page,
        perPage,
      })
      .then(response => mapRelations(response.data))
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }
}
